import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { LogOut, X, Menu, Lock } from 'lucide-react'

export default function Sidebar({ items, currentPage, onNavigate }) {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [open, setOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) setOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close sidebar when navigating on mobile
  const handleNavigate = (id) => {
    onNavigate(id)
    if (isMobile) setOpen(false)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const initials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

  const roleColor = { admin: '#E8614A', instructor: '#0EAAA4', customer: '#2389D4' }

  const SidebarContent = () => (
    <div style={{
      width: 240,
      height: '100%',
      background: '#0B1E3D',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    }}>
      {/* Logo + close button on mobile */}
      <div style={{
        padding: '20px 20px 16px',
        paddingTop: 'calc(20px + env(safe-area-inset-top))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <div style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 20,
          fontWeight: 900,
          color: '#E8614A',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          letterSpacing: 0.5,
        }}>
          <div style={{
            width: 28, height: 28,
            background: '#E8614A',
            borderRadius: '50% 50% 50% 0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transform: 'rotate(-45deg)', fontSize: 14,
          }}>
            <span style={{ transform: 'rotate(45deg)', display: 'block' }}>🌊</span>
          </div>
          WaveSync
        </div>
        {isMobile && (
          <button onClick={() => setOpen(false)} style={{
            background: 'rgba(255,255,255,0.08)', border: 'none',
            borderRadius: 8, width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'rgba(255,255,255,0.6)',
          }}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
        {items.map((item, i) => {
          if (item.type === 'section') {
            return (
              <div key={i} style={{
                fontSize: '0.62rem', fontWeight: 700,
                color: 'rgba(255,255,255,0.25)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                padding: '14px 10px 6px',
              }}>
                {item.label}
              </div>
            )
          }
          const active = currentPage === item.id
          return (
            <button
              key={item.id}
              onClick={() => handleNavigate(item.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10, border: 'none',
                background: active ? 'rgba(232,97,74,0.15)' : 'transparent',
                color: active ? '#E8614A' : item.locked ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.55)',
                cursor: 'pointer', textAlign: 'left',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.875rem', fontWeight: active ? 700 : 500,
                marginBottom: 2,
                borderLeft: active ? '3px solid #E8614A' : '3px solid transparent',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
            >
              <item.icon size={17} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge && (
                <span style={{
                  background: '#E8614A', color: 'white',
                  borderRadius: 50, minWidth: 18, height: 18,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.62rem', fontWeight: 700, padding: '0 4px',
                }}>
                  {item.badge}
                </span>
              )}
              {item.locked && <Lock size={12} style={{ opacity: 0.4, flexShrink: 0 }} />}
            </button>
          )
        })}
      </nav>

      {/* Footer */}
      <div style={{
        padding: '14px 14px',
        borderTop: '1px solid rgba(255,255,255,0.07)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: roleColor[profile?.role] || '#888',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.72rem', fontWeight: 700, color: 'white',
          }}>
            {initials(profile?.full_name)}
          </div>
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {profile?.full_name || 'User'}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', textTransform: 'capitalize' }}>
              {profile?.role}
            </div>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', borderRadius: 8, border: 'none',
            background: 'rgba(255,255,255,0.05)',
            color: 'rgba(255,255,255,0.45)',
            cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
            fontSize: '0.8rem', fontWeight: 500,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)' }}
        >
          <LogOut size={15} />
          Sign Out
        </button>
      </div>
    </div>
  )

  // Mobile: hamburger + slide-in drawer
  if (isMobile) {
    return (
      <>
        {/* Hamburger button — fixed top left */}
        <button
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed', top: 'calc(12px + env(safe-area-inset-top))', left: 12, zIndex: 200,
            width: 40, height: 40, borderRadius: 10,
            background: '#0B1E3D',
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'white',
            boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
          }}
        >
          <Menu size={18} />
        </button>

        {/* Backdrop */}
        {open && (
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 300,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(2px)',
            }}
          />
        )}

        {/* Slide-in drawer */}
        <div style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          zIndex: 400, width: 240,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: open ? '4px 0 24px rgba(0,0,0,0.3)' : 'none',
        }}>
          <SidebarContent />
        </div>
      </>
    )
  }

  // Desktop: static sidebar
  return (
    <div style={{
      width: 240, flexShrink: 0, height: '100vh',
      position: 'sticky', top: 0,
      boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
    }}>
      <SidebarContent />
    </div>
  )
}
